import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container"]
  static values = { existing: Array, questionType: String }

  connect() {
    if (this.existingValue && this.existingValue.length > 0) {
      this.loadExistingOptions()
    } else {
      // Start with 2 empty options
      this.addOption()
      this.addOption()
    }
    
    // Listen for question type changes
    this.setupQuestionTypeListener()
  }

  setupQuestionTypeListener() {
    const questionTypeSelect = this.element.closest('form').querySelector('select[name*="question_type"]')
    if (questionTypeSelect) {
      questionTypeSelect.addEventListener('change', () => {
        // Update the data attribute to keep it in sync
        this.questionTypeValue = questionTypeSelect.value
        this.updateOptionsForQuestionType()
      })
    }
  }

  getQuestionType() {
    // If we have a questionType value (from data attribute), use it
    if (this.hasQuestionTypeValue && this.questionTypeValue) {
      return this.questionTypeValue
    }
    
    // Otherwise, try to get it from the DOM
    const questionTypeSelect = this.element.closest('form').querySelector('select[name*="question_type"]')
    return questionTypeSelect ? questionTypeSelect.value : 'checkbox'
  }

  updateOptionsForQuestionType() {
    const questionType = this.getQuestionType()
    
    // Get all existing options data
    const existingOptions = this.getExistingOptionsData()
    
    // Clear container
    this.containerTarget.innerHTML = ''
    
    // If switching to radio and multiple options are marked correct, keep only the first one
    if (questionType === 'radio') {
      let foundCorrect = false
      existingOptions.forEach(option => {
        if (option.correct && !foundCorrect) {
          foundCorrect = true
        } else if (option.correct && foundCorrect) {
          option.correct = false
        }
      })
    }
    
    // Re-add options with appropriate controls
    existingOptions.forEach(option => {
      this.addOptionWithData(option.text, option.correct)
    })
  }

  getExistingOptionsData() {
    const options = []
    const optionElements = this.containerTarget.querySelectorAll('.quiz-option')
    
    optionElements.forEach(element => {
      const textInput = element.querySelector('input[type="text"]')
      const correctInput = element.querySelector('input[type="checkbox"], input[type="radio"]')
      
      if (textInput && correctInput) {
        options.push({
          text: textInput.value,
          correct: correctInput.checked
        })
      }
    })
    
    return options
  }

  loadExistingOptions() {
    this.existingValue.forEach(option => {
      this.addOptionWithData(option.text, option.correct)
    })
  }

  addOption() {
    this.addOptionWithData('', false)
  }

  addOptionWithData(text = '', correct = false) {
    const index = this.containerTarget.children.length
    const questionType = this.getQuestionType()
    const optionHtml = this.createOptionHtml(index, text, correct, questionType)
    this.containerTarget.insertAdjacentHTML('beforeend', optionHtml)
  }

  removeOption(event) {
    const option = event.target.closest('.quiz-option')
    option.remove()
    this.updateIndices()
  }

  handleCorrectChange(event) {
    const questionType = this.getQuestionType()
    
    // For radio questions, ensure only one option is marked as correct
    if (questionType === 'radio' && event.target.checked) {
      const allCorrectInputs = this.containerTarget.querySelectorAll('input[type="radio"][name*="correct"]')
      allCorrectInputs.forEach(input => {
        if (input !== event.target) {
          input.checked = false
        }
      })
    }
  }

  updateIndices() {
    const options = this.containerTarget.querySelectorAll('.quiz-option')
    options.forEach((option, index) => {
      const inputs = option.querySelectorAll('input, textarea')
      inputs.forEach(input => {
        const name = input.name
        if (name) {
          input.name = name.replace(/\[\d+\]/, `[${index}]`)
        }
      })
    })
  }

  createOptionHtml(index, text = '', correct = false, questionType = 'checkbox') {
    const inputType = questionType === 'radio' ? 'radio' : 'checkbox'
    const inputClass = questionType === 'radio' ? 'radio radio-success' : 'checkbox checkbox-success'
    const correctName = questionType === 'radio' 
      ? 'guide_quiz_question[correct_option]'
      : `guide_quiz_question[options][${index}][correct]`
    
    return `
      <div class="quiz-option bg-base-50 border border-base-200 rounded-lg p-4 hover:bg-base-100 transition-colors">
        <div class="flex items-start gap-3">
          <div class="flex-shrink-0 mt-2">
            <div class="badge badge-outline badge-sm">
              ${index + 1}
            </div>
          </div>
          
          <div class="flex-1">
            <input type="text" 
                   name="guide_quiz_question[options][${index}][text]" 
                   value="${text}"
                   placeholder="Enter option text..."
                   class="input input-bordered w-full"
                   required />
          </div>
          
          <div class="flex items-center gap-3">
            <label class="label cursor-pointer justify-start gap-2 min-w-fit">
              ${questionType === 'checkbox' ? `
                <input type="hidden" 
                       name="guide_quiz_question[options][${index}][correct]" 
                       value="false" />
                <input type="checkbox" 
                       name="guide_quiz_question[options][${index}][correct]" 
                       value="true"
                       ${correct ? 'checked' : ''}
                       class="checkbox checkbox-success" />
              ` : `
                <input type="radio" 
                       name="${correctName}" 
                       value="${index}"
                       ${correct ? 'checked' : ''}
                       class="radio radio-success"
                       data-action="change->quiz-options#handleCorrectChange" />
              `}
              <span class="label-text text-sm font-medium">Correct</span>
            </label>
            
            <button type="button" 
                    data-action="click->quiz-options#removeOption"
                    class="btn btn-sm btn-ghost text-error"
                    title="Remove this option">
              <iconify-icon icon="ph:trash"></iconify-icon>
            </button>
          </div>
        </div>
      </div>
    `
  }
} 